import CircularProgress from "@mui/material/CircularProgress";
import { scoreBand } from "./qualityApi";
import type { FieldQuality } from "./useFieldQuality";

const NOTE_STYLE = {
    marginTop: 7,
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    fontSize: 11.5,
    lineHeight: 1.5
} as const;

/** The evaluation block under a scored narrative field: score, rubric band,
 * and the model's coaching sentence. A failure reports itself here rather than
 * in a toast — a background quality check shouldn't interrupt the form. */
export function FieldQualityNote({ label, quality }: { label: string; quality: FieldQuality }) {
    if (quality.status === "idle") return null;

    if (quality.status === "scoring") {
        return (
            <div style={NOTE_STYLE} role="status" aria-live="polite" aria-label={`${label} evaluation`}>
                <CircularProgress size={11} sx={{ color: "var(--np-brand)", marginTop: "2px" }} />
                <span style={{ color: "var(--fg3)", fontWeight: 600 }}>Evaluating…</span>
            </div>
        );
    }

    if (quality.status === "error") {
        return (
            <div style={NOTE_STYLE} role="status" aria-live="polite" aria-label={`${label} evaluation`}>
                <span style={{ color: "var(--fg4)" }}>{quality.message}</span>
            </div>
        );
    }

    const band = scoreBand(quality.score);
    return (
        <div style={NOTE_STYLE} role="status" aria-live="polite" aria-label={`${label} evaluation`}>
            <span
                style={{
                    flexShrink: 0,
                    padding: "2px 7px",
                    borderRadius: 999,
                    border: `1px solid ${band.color}`,
                    color: band.color,
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums"
                }}
            >
                {quality.score} / 100
            </span>
            <span style={{ flexShrink: 0, color: band.color, fontWeight: 700, marginTop: 2 }}>{band.label}</span>
            <span style={{ color: "var(--fg3)", marginTop: 2 }}>{quality.feedback}</span>
        </div>
    );
}
